import dayjs from 'dayjs';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'];

export const formatDate = (dateString) => {
  if (!dateString || dateString === '-') return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} ${hours}:${mins} WIB`;
};

// Format ringkas untuk kolom Lokasi Muat/Bongkar: "07-Mei-26 15:00"
// Return null jika tanggal tidak valid atau zero-date backend (year < 2000)
export const formatShortDate = (dateString) => {
  if (!dateString || dateString === '-') return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  if (date.getFullYear() < 2000) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${mins}`;
};

// Fallback chain untuk waktu aktual — pakai field utama dulu, fallback ke alias
// (in_time vs ata vs actual_arrival, dst). Konsisten dengan helper di
// BuktiPenugasanDialog & ShipmentDetailView.
export const pickActualTime = (...candidates) => {
  for (const c of candidates) {
    if (!c) continue;
    const d = new Date(c);
    if (!isNaN(d.getTime()) && d.getFullYear() >= 2000) return c;
  }
  return '';
};

export const diffMinutes = (actual, target) => {
  if (!actual || !target) return null;
  const a = new Date(actual),
    t = new Date(target);
  if (isNaN(a) || isNaN(t)) return null;
  return Math.floor((a - t) / 60000);
};

export const fmtDelay = (mins) => {
  if (mins === null || mins === undefined) return null;
  const abs = Math.abs(mins);
  const d = Math.floor(abs / (60 * 24));
  const h = Math.floor((abs % (60 * 24)) / 60);
  const m = abs % 60;
  const parts = [];
  if (d > 0) parts.push(`${d} hari`);
  if (h > 0) parts.push(`${h} jam`);
  if (m > 0 || parts.length === 0) parts.push(`${m} menit`);
  return parts.join(' ');
};

export const toGeofenceTime = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
};

// ─── DATE_PRESETS ─────────────────────────────────────────────────────────────
// Preset filter range tanggal untuk Quick Assignment (& page lain yang reuse).
// Tiap preset:
//   - id     : key unik (juga di-persist di localStorage)
//   - label  : teks display di dropdown
//   - build(): function → { from: Date|null, to: Date|null }
//
// "Semua periode" = preset 'all' dengan from/to null → backend tidak apply range.
// Preset "Hari Ini" / "7 Hari Terakhir" / dll dihitung relatif ke saat function
// dipanggil (NOT stale — selalu fresh saat user pilih).
export const DATE_PRESETS = [
  {
    id: 'today',
    label: 'Hari Ini',
    build: () => {
      const t = new Date();
      const s = new Date(t);
      s.setHours(0, 0, 0, 0);
      const e = new Date(t);
      e.setHours(23, 59, 59, 999);
      return { from: s, to: e };
    }
  },
  {
    id: 'yesterday',
    label: 'Kemarin',
    build: () => {
      const t = new Date();
      t.setDate(t.getDate() - 1);
      const s = new Date(t);
      s.setHours(0, 0, 0, 0);
      const e = new Date(t);
      e.setHours(23, 59, 59, 999);
      return { from: s, to: e };
    }
  },
  {
    id: 'last7days',
    label: '7 Hari Terakhir',
    build: () => {
      const e = new Date();
      e.setHours(23, 59, 59, 999);
      const s = new Date();
      s.setDate(s.getDate() - 6);
      s.setHours(0, 0, 0, 0);
      return { from: s, to: e };
    }
  },
  {
    id: 'last30days',
    label: '30 Hari Terakhir',
    build: () => {
      const e = new Date();
      e.setHours(23, 59, 59, 999);
      const s = new Date();
      s.setDate(s.getDate() - 29);
      s.setHours(0, 0, 0, 0);
      return { from: s, to: e };
    }
  },
  {
    id: 'thismonth',
    label: 'Bulan Ini',
    build: () => {
      const t = new Date();
      const s = new Date(t.getFullYear(), t.getMonth(), 1, 0, 0, 0, 0);
      const e = new Date(t.getFullYear(), t.getMonth() + 1, 0, 23, 59, 59, 999);
      return { from: s, to: e };
    }
  },
  {
    id: 'lastmonth',
    label: 'Bulan Lalu',
    build: () => {
      const t = new Date();
      const s = new Date(t.getFullYear(), t.getMonth() - 1, 1, 0, 0, 0, 0);
      const e = new Date(t.getFullYear(), t.getMonth(), 0, 23, 59, 59, 999);
      return { from: s, to: e };
    }
  }
];

export const isZeroDate = (date) => {
  return !date || !dayjs(date).isValid() || dayjs(date).year() <= 1900;
};
